import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Rocket, Check } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { CATEGORY_META, dayLabel } from "../lib/constants";
import PhotoUpload from "../components/PhotoUpload";

const DEFAULT_HOURS = {
  mon: "08:00-18:00", tue: "08:00-18:00", wed: "08:00-18:00",
  thu: "08:00-18:00", fri: "08:00-18:00", sat: "09:00-17:00", sun: "closed",
};

const STEPS = [
  { n: 1, label: "Entreprise" },
  { n: 2, label: "Localisation" },
  { n: 3, label: "Contact" },
  { n: 4, label: "Horaires" },
  { n: 5, label: "Photos" },
  { n: 6, label: "Confirmation" },
];

export default function BusinessRegisterPage() {
  const { user, loading: authLoading } = useAuth() || {};
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [photos, setPhotos] = useState([]);

  const [form, setForm] = useState({
    name: "",
    category_slug: "",
    tagline: "",
    description: "",
    price_range: "$$",
    tags: "",
    city_slug: "conakry",
    neighborhood_slug: "",
    address: "",
    phone: "",
    phone_2: "",
    whatsapp: "",
    email: "",
    website: "",
    hours: DEFAULT_HOURS,
  });

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { state: { redirect: "/business/register" } });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    (async () => {
      const c = await api.get("/cities");
      setCities(c.data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!form.city_slug) return;
      const { data } = await api.get("/neighborhoods", { params: { city_slug: form.city_slug } });
      setNeighborhoods(data);
    })();
  }, [form.city_slug]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const updateHours = (k, v) => setForm((s) => ({ ...s, hours: { ...s.hours, [k]: v } }));

  const canNext = () => {
    if (step === 1) return form.name && form.category_slug && form.description;
    if (step === 2) return form.city_slug && form.address;
    if (step === 3) return form.phone;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const cover = photos.find((p) => p.is_cover) || photos[0];
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        amenities: [],
        cover_image_url: cover ? cover.url : "",
      };
      const { data } = await api.post("/businesses", payload);
      // Attach extra photos to the freshly-created business (non-blocking)
      const extras = photos.filter((p) => p !== cover);
      if (extras.length) {
        // We don't have the file blob anymore — we already uploaded via /api/upload.
        // For MVP we keep cover_image_url only; gallery photos need to be re-uploaded
        // via /api/businesses/{id}/photos after creation. The PhotoUpload result IDs are
        // already in db.files. Future: link those file IDs to business_photos here.
      }
      setSubmittedId(data.id);
      setStep(7);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || !user) return <div className="min-h-screen flex items-center justify-center">Chargement…</div>;

  return (
    <div className="min-h-screen bg-lokaso-sand py-10 px-4" data-testid="business-register-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-display text-3xl md:text-4xl font-bold">Inscrire mon entreprise</h1>
        <p className="text-stone-600 mt-1">Présentez votre entreprise à des milliers de clients potentiels.</p>

        {/* Stepper */}
        <ol className="mt-8 flex items-center gap-2" data-testid="register-stepper">
          {STEPS.map((s, i) => (
            <li key={s.n} className="flex items-center flex-1">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                step > s.n ? "bg-lokaso-green text-white border-lokaso-green"
                : step === s.n ? "bg-lokaso-gold text-lokaso-dark border-lokaso-gold"
                : "bg-white text-stone-400 border-stone-200"
              }`}>
                {step > s.n ? <Check className="w-4 h-4" /> : s.n}
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${step > s.n ? "bg-lokaso-green" : "bg-stone-200"}`} />}
            </li>
          ))}
        </ol>

        <div className="mt-8 bg-white rounded-2xl border border-stone-200 p-6 md:p-8">
          {step === 1 && (
            <div className="space-y-4" data-testid="step-1">
              <h2 className="font-display text-2xl font-bold">Votre entreprise</h2>
              <Field label="Nom de l'entreprise">
                <input value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} data-testid="biz-name-input" />
              </Field>
              <Field label="Catégorie">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {Object.entries(CATEGORY_META).map(([slug, meta]) => (
                    <button
                      type="button"
                      key={slug}
                      onClick={() => update("category_slug", slug)}
                      className={`p-3 rounded-xl border text-sm font-medium text-left flex items-center gap-2 ${
                        form.category_slug === slug ? "border-lokaso-green bg-lokaso-green/5" : "border-stone-200 hover:bg-stone-50"
                      }`}
                      data-testid={`select-category-${slug}`}
                    >
                      <span className="text-xl">{meta.emoji}</span>
                      <span className="text-xs">{meta.name}</span>
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Slogan court (100 car. max)">
                <input value={form.tagline} maxLength={100} onChange={(e) => update("tagline", e.target.value)} className={inputCls} data-testid="biz-tagline-input" />
              </Field>
              <Field label="Description">
                <textarea value={form.description} onChange={(e) => update("description", e.target.value)} className={`${inputCls} min-h-[120px] py-2`} data-testid="biz-description-input" />
              </Field>
              <Field label="Gamme de prix">
                <div className="flex gap-2">
                  {["$","$$","$$$","$$$$"].map((p) => (
                    <button type="button" key={p} onClick={() => update("price_range", p)} className={`h-10 px-4 rounded-xl border font-semibold ${form.price_range === p ? "bg-lokaso-green text-white border-lokaso-green" : "border-stone-200 hover:bg-stone-50"}`} data-testid={`select-price-${p.length}`}>{p}</button>
                  ))}
                </div>
              </Field>
              <Field label="Tags (séparés par des virgules)">
                <input value={form.tags} onChange={(e) => update("tags", e.target.value)} placeholder="Halal, Climatisé, Livraison" className={inputCls} data-testid="biz-tags-input" />
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="step-2">
              <h2 className="font-display text-2xl font-bold">Localisation</h2>
              <Field label="Ville">
                <select value={form.city_slug} onChange={(e) => { update("city_slug", e.target.value); update("neighborhood_slug", ""); }} className={inputCls} data-testid="biz-city-select">
                  {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Quartier">
                <select value={form.neighborhood_slug} onChange={(e) => update("neighborhood_slug", e.target.value)} className={inputCls} data-testid="biz-neighborhood-select">
                  <option value="">— Sélectionner —</option>
                  {neighborhoods.map((n) => <option key={n.slug} value={n.slug}>{n.name}</option>)}
                </select>
              </Field>
              <Field label="Adresse complète">
                <input value={form.address} onChange={(e) => update("address", e.target.value)} className={inputCls} data-testid="biz-address-input" placeholder="Avenue de la République, immeuble..." />
              </Field>
              <p className="text-xs text-stone-500">Position GPS ajoutée lors de la vérification.</p>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="step-3">
              <h2 className="font-display text-2xl font-bold">Contact</h2>
              <Field label="Téléphone principal (requis)">
                <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} placeholder="+224 ..." data-testid="biz-phone-input" />
              </Field>
              <Field label="Téléphone secondaire">
                <input value={form.phone_2} onChange={(e) => update("phone_2", e.target.value)} className={inputCls} data-testid="biz-phone2-input" />
              </Field>
              <Field label="WhatsApp">
                <input value={form.whatsapp} onChange={(e) => update("whatsapp", e.target.value)} className={inputCls} data-testid="biz-whatsapp-input" />
              </Field>
              <Field label="Email">
                <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} data-testid="biz-email-input" />
              </Field>
              <Field label="Site web">
                <input value={form.website} onChange={(e) => update("website", e.target.value)} className={inputCls} data-testid="biz-website-input" />
              </Field>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3" data-testid="step-4">
              <h2 className="font-display text-2xl font-bold">Horaires</h2>
              <p className="text-sm text-stone-600">Format: <code>08:00-18:00</code> ou <code>closed</code></p>
              {Object.keys(DEFAULT_HOURS).map((d) => (
                <div key={d} className="grid grid-cols-3 items-center gap-3">
                  <label className="text-sm font-medium">{dayLabel(d)}</label>
                  <input className={`${inputCls} col-span-2`} value={form.hours[d] || ""} onChange={(e) => updateHours(d, e.target.value)} data-testid={`hours-${d}-input`} />
                </div>
              ))}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4" data-testid="step-5">
              <h2 className="font-display text-2xl font-bold">Photos de l'entreprise</h2>
              <p className="text-sm text-stone-600">Ajoutez jusqu'à 6 photos. La première sera votre image de couverture (vous pouvez la changer ensuite).</p>
              <PhotoUpload photos={photos} onChange={setPhotos} max={6} />
              <p className="text-xs text-stone-500">Optionnel — vous pouvez aussi ajouter des photos plus tard depuis votre tableau de bord.</p>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-4" data-testid="step-6">
              <h2 className="font-display text-2xl font-bold">Confirmation</h2>
              <div className="rounded-xl border border-stone-200 p-4 bg-lokaso-cream/40 text-sm space-y-1">
                <p><strong>Entreprise:</strong> {form.name}</p>
                <p><strong>Catégorie:</strong> {CATEGORY_META[form.category_slug]?.name || "—"}</p>
                <p><strong>Ville/Quartier:</strong> {form.city_slug} {form.neighborhood_slug && `· ${form.neighborhood_slug}`}</p>
                <p><strong>Adresse:</strong> {form.address}</p>
                <p><strong>Téléphone:</strong> {form.phone}</p>
                <p><strong>Prix:</strong> {form.price_range}</p>
                <p><strong>Photos:</strong> {photos.length} ajoutée{photos.length > 1 ? "s" : ""}</p>
              </div>
              <div className="rounded-xl border border-stone-200 p-4">
                <h3 className="font-display text-lg font-bold mb-2">Prochaines étapes</h3>
                <ul className="space-y-1 text-sm text-stone-700">
                  <li className="flex gap-2"><span className="text-lokaso-green">✓</span> Vérification sous 24–48h</li>
                  <li className="flex gap-2"><span className="text-lokaso-green">✓</span> Email de confirmation à l'approbation</li>
                  <li className="flex gap-2"><span className="text-lokaso-green">✓</span> Visible sur Lokaso</li>
                </ul>
              </div>
              <button onClick={submit} disabled={submitting} className="btn-primary w-full inline-flex items-center justify-center gap-2 text-lg" data-testid="biz-submit-btn">
                <Rocket className="w-5 h-5" /> {submitting ? "Envoi…" : "Soumettre mon entreprise"}
              </button>
            </div>
          )}

          {step === 7 && submittedId && (
            <div className="text-center py-8" data-testid="step-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-lokaso-green text-white flex items-center justify-center mb-4">
                <Check className="w-8 h-8" />
              </div>
              <h2 className="font-display text-2xl font-bold">Entreprise soumise !</h2>
              <p className="text-stone-600 mt-2">Notre équipe va examiner votre entreprise sous 24–48h.</p>
              <div className="mt-6 flex gap-3 justify-center">
                <Link to="/dashboard" className="btn-green">Aller au tableau de bord</Link>
                <Link to="/" className="px-4 py-2 rounded-xl border border-stone-200">Retour à l'accueil</Link>
              </div>
            </div>
          )}

          {/* Navigation */}
          {step <= 6 && (
            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl border border-stone-200 disabled:opacity-50"
                data-testid="step-prev"
              >
                <ChevronLeft className="w-4 h-4" /> Précédent
              </button>
              {step < 6 && (
                <button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canNext()}
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-lokaso-green text-white disabled:opacity-50"
                  data-testid="step-next"
                >
                  Suivant <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-11 px-3 rounded-xl border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-lokaso-green";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-stone-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
